import React from 'react';
import { Eye, EyeOff, Loader2, Play } from 'lucide-react';

import { formatResizeDisplay, formatTokenCount, toFiniteCount } from '../../utils/formatters';
import { getGroupImageUrls, getGroupKey, normalizeHistoryImageKey } from './historyUtils';

const HistoryGroupHeader = ({
  group,
  currentUsername,
  disabledImageKeys,
  rerunningGroup,
  onOpenImageGroup,
  onToggleImage,
  onRerunGroup,
}) => {
  const gKey = getGroupKey(group);
  const allUrls = getGroupImageUrls(group);
  const groupDisabled = new Set(
    (Array.isArray(disabledImageKeys[gKey]) ? disabledImageKeys[gKey] : [])
      .map((key) => normalizeHistoryImageKey(key))
      .filter(Boolean),
  );
  const enabledKeySet = new Set(
    allUrls
      .map((url) => normalizeHistoryImageKey(url))
      .filter((key) => key && !groupDisabled.has(key)),
  );
  const enabledCount = enabledKeySet.size;
  const hasDisabled = groupDisabled.size > 0;
  const isRerunning = rerunningGroup === gKey;
  const representativeItem = group.items?.[0] || {};
  const usage = (group.usage && typeof group.usage === 'object')
    ? group.usage
    : ((representativeItem.usage && typeof representativeItem.usage === 'object') ? representativeItem.usage : {});
  const inputTokens = toFiniteCount(
    usage.prompt_tokens ?? usage.input_tokens ?? group.input_tokens_consumed ?? representativeItem.input_tokens_consumed ?? null,
  );
  const outputTokens = toFiniteCount(
    usage.completion_tokens ?? usage.output_tokens ?? group.output_tokens_consumed ?? representativeItem.output_tokens_consumed ?? null,
  );
  const rawTotalTokens = toFiniteCount(
    usage.total_tokens ?? group.total_tokens_consumed ?? representativeItem.total_tokens_consumed ?? null,
  );
  const totalTokens = (
    inputTokens === null && outputTokens === null
      ? null
      : (rawTotalTokens !== null
        ? rawTotalTokens
        : (inputTokens !== null && outputTokens !== null ? (inputTokens + outputTokens) : null))
  );
  const sourceRaw = String(group.analysis_source ?? representativeItem.analysis_source ?? '').trim().toLowerCase();
  const cacheHit = group.cache_hit === true || representativeItem.cache_hit === true || sourceRaw === 'cache';
  const sourceLabel = cacheHit ? 'CACHE' : 'LIVE';
  const sourceClass = cacheHit
    ? 'bg-orange-100 text-orange-700 border border-orange-200'
    : 'bg-green-100 text-green-700 border border-green-200';
  const useEbayRag = (
    group.use_ebay_rag !== undefined && group.use_ebay_rag !== null
      ? Boolean(group.use_ebay_rag)
      : Boolean(representativeItem.use_ebay_rag)
  );
  const ragRefs = Math.max(0, Number(group.ebay_rag_result_count ?? representativeItem.ebay_rag_result_count ?? 0) || 0);
  const useNotesRag = (
    group.use_notes_rag !== undefined && group.use_notes_rag !== null
      ? Boolean(group.use_notes_rag)
      : Boolean(representativeItem.use_notes_rag)
  );
  const notesRefs = Math.max(0, Number(group.notes_rag_example_count ?? representativeItem.notes_rag_example_count ?? 0) || 0);
  const assumeHiddenItems = (
    group.assume_hidden_items !== undefined && group.assume_hidden_items !== null
      ? Boolean(group.assume_hidden_items)
      : Boolean(representativeItem.assume_hidden_items)
  );

  return (
    <div className="bg-slate-50 p-3 sm:p-4 border-b border-slate-200">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <div className="flex gap-2 flex-wrap">
          {allUrls.map((url, idx) => {
            const imageKey = normalizeHistoryImageKey(url);
            const isDisabled = imageKey ? groupDisabled.has(imageKey) : false;
            const disablingLastEnabledImage = !isDisabled && enabledCount <= 1;
            return (
              <div key={idx} className="relative">
                <div
                  className="cursor-pointer"
                  onClick={() => onOpenImageGroup(url, allUrls, group.items)}
                >
                  <img
                    src={url}
                    alt={`Image ${idx + 1}`}
                    className={`w-14 h-14 sm:w-16 sm:h-16 object-cover rounded border-2 shadow-sm transition-all ${
                      isDisabled ? 'opacity-30 grayscale border-red-300' : 'border-white hover:scale-105'
                    }`}
                  />
                </div>
                {allUrls.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleImage(gKey, url, allUrls);
                    }}
                    disabled={disablingLastEnabledImage}
                    className={`absolute -top-1.5 -right-1.5 p-0.5 rounded-full border shadow-sm transition-colors z-10 ${
                      disablingLastEnabledImage
                        ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed'
                        : (isDisabled
                          ? 'bg-red-100 border-red-300 text-red-600 hover:bg-red-200'
                          : 'bg-green-100 border-green-300 text-green-600 hover:bg-green-200')
                    }`}
                    title={disablingLastEnabledImage ? 'At least one image must stay enabled' : (isDisabled ? 'Enable image' : 'Disable image')}
                  >
                    {isDisabled ? <EyeOff size={10} /> : <Eye size={10} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold text-slate-700">{new Date(group.timestamp).toLocaleString()}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-[10px] font-mono text-slate-500 bg-white border px-2 py-0.5 rounded">{group.model_name}</span>
            {group.username && (group.user_deleted || group.username !== currentUsername) && (
              <span
                className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                  group.user_deleted
                    ? 'text-rose-700 bg-rose-50 border-rose-200'
                    : 'text-slate-700 bg-slate-100 border-slate-200'
                }`}
                title={group.user_deleted
                  ? `Analyzed by ${group.username} (account deleted)`
                  : `Analyzed by ${group.username}`}
              >
                By: {group.username}{group.user_deleted ? ' (deleted)' : ''}
              </span>
            )}
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
              Resize: {formatResizeDisplay(group.resize_mode)}
            </span>
            <span className="text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded">
              Compression: {group.compression_mode || 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-cyan-700 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded">
              eBay RAG: {useEbayRag ? `on (${ragRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">
              Notes RAG: {useNotesRag ? `on (${notesRefs} refs)` : 'off'}
            </span>
            <span className="text-[10px] font-mono text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-100 px-2 py-0.5 rounded">
              Hidden Items: {assumeHiddenItems ? 'on' : 'off'}
            </span>
            <span className="text-[10px] font-mono text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
              {group.analysis_duration_ms !== undefined && group.analysis_duration_ms !== null
                ? `${Number(group.analysis_duration_ms).toFixed(0)}ms`
                : 'N/A'}
            </span>
            <span className="text-[10px] font-mono text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded">
              {group.analysis_cost_display !== undefined
                ? group.analysis_cost_display
                : (group.analysis_cost !== undefined && group.analysis_cost !== null
                  ? `$${Number(group.analysis_cost).toFixed(4)}`
                  : 'N/A')}
            </span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${sourceClass}`}>
              Src: {sourceLabel}
            </span>
            <span className="text-[10px] font-mono text-blue-700 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded">
              In Tok: {formatTokenCount(inputTokens)}
            </span>
            <span className="text-[10px] font-mono text-violet-700 bg-violet-100 border border-violet-200 px-2 py-0.5 rounded">
              Out Tok: {formatTokenCount(outputTokens)}
            </span>
            <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded">
              Tot Tok: {formatTokenCount(totalTokens)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {hasDisabled && (
            <button
              onClick={() => onRerunGroup(group)}
              disabled={isRerunning || enabledCount === 0}
              className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border transition-colors ${
                isRerunning || enabledCount === 0
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
              }`}
              title={`Re-run with ${enabledCount}/${allUrls.length} images`}
            >
              {isRerunning ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Re-run ({enabledCount}/{allUrls.length})
            </button>
          )}
          <div className="text-right">
            <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Items Found</div>
            <div className="text-2xl font-bold text-slate-800">{group.items.length}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryGroupHeader;
